import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";

import { useState, useEffect } from "react";
import { getAcademicYear } from "../utils/academicYear";
import { printHtml } from "../utils/htmlToPrint";
import AuthService from "../Auth/AuthService";
import CheckMarksheetReceipt from "../components/student-certificates/CheckMarksheetReceipt";
import ResultSheet from "../components/MonthlyTest/ResultSheet";

export default function CheckResultPage() {
    const [search, setSearch] = useState({
        searchBy: "rollNo",
        keyword: "",
        academicYear: getAcademicYear(),
        exam: "final",
        testType: "Monthly Test - October",
    });

    const [student, setStudent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [temID, setTemID] = useState(null);

    const [errors, setErrors] = useState({
        keyword: "",
        academicYear: "",
        exam: "",
        testType: "",
    });

    const validateField = (name, value) => {
        switch (name) {
            case "keyword":
                if (!value || value.trim() === "") {
                    return "Please enter a number";
                }

                if (!/^\d+$/.test(value.trim())) {
                    return "Please enter only numbers";
                }

                return "";

            case "academicYear":
                if (!value || !value.trim()) {
                    return "Please select an academic year";
                }

                return "";

            case "exam":
                if (!value || !value.trim()) {
                    return "Please select an exam type";
                }

                return "";

            default:
                return "";
        }
    };

    const isFormValid =
        !validateField("keyword", search.keyword) &&
        !validateField("academicYear", search.academicYear) &&
        !validateField("exam", search.exam);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setSearch((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, value),
        }));

        // Clear previous results when search criteria change.
        setStudent([]);
        setHasSearched(false);
        setTemID(null);
    };

    // Keep validation errors synchronized with the form.
    useEffect(() => {
        setErrors({
            keyword: validateField("keyword", search.keyword),
            academicYear: validateField(
                "academicYear",
                search.academicYear
            ),
            exam: validateField("exam", search.exam),
        });
    }, [search.keyword, search.academicYear, search.exam]);

    const handleSearch = async () => {
        if (!isFormValid || loading) {
            return;
        }

        setLoading(true);
        setHasSearched(true);
        setStudent([]);
        setTemID(null);

        try {

            var url;
            if (search.exam === "tests") {
                url = AuthService.pathUrl(
                    "POST",
                    "/monthly-tests/check-result"
                );

            } else {
                url = AuthService.pathUrl(
                    "POST",
                    "/marksheets/check-result"
                );

            }

            const result = await AuthService.restPost(url, {
                ...search,
                keyword: search.keyword.trim(),
            });

            if (result) {
                const resultArray = Array.isArray(result)
                    ? result
                    : [result];

                setStudent(resultArray.filter(Boolean));
            } else {
                setStudent([]);
            }
        } catch (error) {
            console.error("Error fetching student result:", error);
            setStudent([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch();
        }
    };

    const handlePrint = () => {
        if (temID == null) {
            console.error("Marksheet template ID is not available.");
            return;
        }
        printHtml(temID);
        setStudent([]);
        window.location.reload();
    };

    return (
        <Box
            sx={{
                // maxWidth: 1100,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    width: "100%",
                    overflow: "visible",
                }}
            >
                <CardContent
                    sx={{
                        "@media print": {
                            display: "none",
                        },
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={3}
                    >
                        <SchoolIcon
                            sx={{
                                fontSize: 45,
                                color: "#1b1914",
                                mr: 1,
                            }}
                        />

                        <Typography
                            variant="h5"
                            fontWeight="bold"
                        >
                            Check Result Online
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {/* Search By */}
                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                fullWidth
                                label="Search By"
                                name="searchBy"
                                value={search.searchBy}
                                onChange={handleChange}
                            >
                                <MenuItem value="rollNo">
                                    Roll Number
                                </MenuItem>

                                <MenuItem value="studentId">
                                    Student ID No.
                                </MenuItem>
                            </TextField>
                        </Grid>

                        {/* Keyword */}
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                label={
                                    search.searchBy === "rollNo"
                                        ? "Enter Roll Number"
                                        : "Enter Student ID"
                                }
                                name="keyword"
                                value={search.keyword}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                error={Boolean(errors.keyword)}
                                helperText={errors.keyword}
                                required
                                slotProps={{
                                    htmlInput: {
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                    }
                                }}
                            />
                        </Grid>

                        {/* Academic Year */}
                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                fullWidth
                                label="Academic Year"
                                name="academicYear"
                                value={search.academicYear}
                                onChange={handleChange}
                                error={Boolean(errors.academicYear)}
                                helperText={errors.academicYear}
                                required
                            >
                                <MenuItem value={search.academicYear}>
                                    {search.academicYear}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        {/* Exam */}
                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                fullWidth
                                label="Exam"
                                name="exam"
                                value={search.exam}
                                onChange={handleChange}
                                error={Boolean(errors.exam)}
                                helperText={errors.exam}
                                required
                            >
                                <MenuItem value="tests">
                                    Test Series
                                </MenuItem>

                                <MenuItem value="first">
                                    Quarterly
                                </MenuItem>

                                <MenuItem value="second">
                                    Half Quarterly
                                </MenuItem>

                                <MenuItem value="final">
                                    Annual Exam
                                </MenuItem>
                            </TextField>
                        </Grid>

                        {/* testType */}
                        {search.exam === "tests" && (
                            < Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    label={
                                        search.searchBy === "rollNo"
                                            ? "Enter Roll Number"
                                            : "Enter Student ID"
                                    }
                                    name="testType"
                                    value={search.testType}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                    error={Boolean(errors.testType)}
                                    helperText={errors.testType}
                                    required
                                />
                            </Grid>
                        )}


                        {/* Search Button */}
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SearchIcon />
                                    )
                                }
                                sx={{
                                    height: 50,
                                    bgcolor: "#1b1914",
                                    fontWeight: "bold",
                                    "&:hover": {
                                        bgcolor: "#33302a",
                                    },
                                }}
                                onClick={handleSearch}
                                disabled={!isFormValid || loading}
                            >
                                {loading
                                    ? "Checking Result..."
                                    : "Check Result"}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>

                {/* Result */}
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        p={4}
                    >
                        <CircularProgress />
                    </Box>
                ) : student.length > 0 ? (
                    <>
                        {search.exam === "tests" ? (
                            <ResultSheet receiptData={student}
                                setTemID={setTemID} />
                        ) : (
                            <CheckMarksheetReceipt
                                receiptData={student}
                                setTemID={setTemID}
                            />
                        )}
                        <Box
                            textAlign="center"
                            mt={3}
                            mb={3}
                            sx={{
                                "@media print": {
                                    display: "none",
                                },
                            }}
                        >
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: "#3f2d01",
                                    borderRadius: 5,
                                    px: 4,
                                    "&:hover": {
                                        bgcolor: "#5a4108",
                                    },
                                }}
                                onClick={handlePrint}
                                disabled={temID == null}
                            >
                                Print Marksheet
                            </Button>
                        </Box>
                    </>
                ) : hasSearched ? (
                    <Typography
                        color="error"
                        sx={{
                            mt: 2,
                            mb: 3,
                            textAlign: "center",
                        }}
                    >
                        No student found with the given criteria.
                    </Typography>
                ) : null}
            </Card>
        </Box >
    );
}